/**
 * PWA utilities
 *
 * Android PWAs can expose the View Transitions API but freeze/hang when it's used.
 * Treat installed/standalone mode as a hard-disable signal for view transitions.
 */

export const isInstalledPWA = (): boolean => {
  if (typeof window === "undefined") return false;

  const standalone = !!window.matchMedia?.("(display-mode: standalone)").matches;
  const fullscreen = !!window.matchMedia?.("(display-mode: fullscreen)").matches;
  const minimalUi = !!window.matchMedia?.("(display-mode: minimal-ui)").matches;

  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  const androidReferrer = typeof document !== "undefined" && document.referrer.includes("android-app://");
  const startUrlMarker = typeof window !== "undefined" && window.location.search.includes("source=pwa");

  return standalone || fullscreen || minimalUi || iosStandalone || androidReferrer || startUrlMarker;
};

/**
 * Persist a flag once the app has ever been observed running as an
 * installed PWA on this device. Used by BrowserAfterInstallBanner so we
 * can tell users to switch from a browser tab to the installed app.
 */
export const PWA_INSTALLED_FLAG = "lupuslogix_pwa_installed";
export const markPWAInstalledIfStandalone = (): void => {
  if (typeof window === "undefined") return;
  try {
    if (isInstalledPWA()) {
      window.localStorage.setItem(PWA_INSTALLED_FLAG, "true");
    }
  } catch { /* ignore */ }
};
export const wasPWAEverInstalled = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(PWA_INSTALLED_FLAG) === "true";
  } catch {
    return false;
  }
};

/**
 * Classify the current surface so backend can lock cross-surface
 * checkouts (browser vs installed PWA vs native shell).
 */
export const getCheckoutSurface = (): "pwa" | "browser" | "native" => {
  if (typeof window === "undefined") return "browser";
  // Native Capacitor shell exposes window.Capacitor.
  const cap = (window as any).Capacitor;
  if (cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform()) {
    return "native";
  }
  return isInstalledPWA() ? "pwa" : "browser";
};

/**
 * Safe wrapper for startViewTransition.
 * Returns true if a view transition was started.
 */
export const tryStartViewTransition = (cb: () => void): boolean => {
  if (typeof document === "undefined") return false;
  if (isInstalledPWA()) return false;

  const start = (document as Document & { startViewTransition?: (cb: () => void) => unknown })
    .startViewTransition;
  if (!start) return false;

  try {
    start(cb);
    return true;
  } catch {
    return false;
  }
};
